/**
 * Sticky UI preferences — panel collapse, nothing more.
 *
 * Not for anything that matters. These are conveniences: a land user who never
 * reads the KPI numbers should not re-hide them every morning, and one who
 * watches Expiring ≤ 30d should not re-open them. Nothing here is a
 * permission, a filter, or data. A tampered value can only change which
 * chevron a panel starts on.
 *
 * Never throws. `localStorage` is unavailable in private mode and over quota,
 * and losing a chevron preference must not take a page down with it — so every
 * access is guarded and failure just means the preference does not persist.
 *
 * Read once during `useState` initialisation, not in an effect, so the panel
 * renders in its remembered state instead of flashing open and then folding
 * shut.
 */

/** Namespaced so these never collide with the app's other stored values. */
const PREFIX = "bd.ui.";

/**
 * @param {string}  key       short name, namespaced internally
 * @param {boolean} fallback  value when nothing is stored or storage is unusable
 * @returns {boolean}
 */
export const readFlag = (key, fallback = false) => {
  try {
    const raw = window.localStorage?.getItem(PREFIX + key);
    if (raw === null || raw === undefined) return fallback;
    return raw === "1";
  } catch {
    return fallback;
  }
};

/**
 * @param {string}  key
 * @param {boolean} value
 */
export const writeFlag = (key, value) => {
  try {
    window.localStorage?.setItem(PREFIX + key, value ? "1" : "0");
  } catch {
    // Storage unavailable; the preference lasts for this session only.
  }
};

export const KPI_COLLAPSED = "dealsKpi.collapsed";
export const FILTERS_COLLAPSED = "dealsFilters.collapsed";
export const MAP_COLLAPSED = "dealsMap.collapsed";
export const FEEDBACK_KPI_COLLAPSED = "feedbackKpi.collapsed";
export const FEEDBACK_FILTERS_COLLAPSED = "feedbackFilters.collapsed";
export const REPORTS_FILTERS_COLLAPSED = "reportsFilters.collapsed";
